import { logGovernedMcp } from './governedMcpLogger';
import type {
  DataModelBacking,
  DataModelField,
  DataModelOperation,
  DataModelSource,
  DataModelTable,
  McpCapabilityKind,
  PublishSourceKind,
  SelficCapabilityKind,
  WorkflowInputField,
} from './governedMcpTypes';

export const STANDARD_FILE_FIELDS: DataModelField[] = [
  { name: 'file_id', displayName: 'File id', dataType: 'string', nullable: false, isPrimaryKey: true },
  { name: 'name', displayName: 'Name', dataType: 'string', nullable: false },
  { name: 'path', displayName: 'Path', dataType: 'string', nullable: false },
  { name: 'folder_id', displayName: 'Folder id', dataType: 'string', nullable: true },
  { name: 'mime_type', displayName: 'Mime type', dataType: 'string', nullable: true },
  { name: 'modified_at', displayName: 'Modified at', dataType: 'datetime', nullable: true },
];

export function classifyBacking(connectorKey?: string, sourceType?: string, category?: string): DataModelBacking {
  const key = `${connectorKey || ''} ${sourceType || ''}`.toLowerCase();
  const cat = (category || '').toLowerCase();
  if (cat === 'file_storage' || key.includes('google_drive') || key.includes('gdrive') || key.includes('google')) {
    return 'google_drive';
  }
  if (key.includes('postgres') || key.includes('pgsql')) return 'postgres';
  if (key.includes('mssql') || key.includes('sqlserver') || key.includes('sql_server')) return 'mssql';
  if (key.includes('mysql')) return 'mysql';
  if (key.includes('drive')) return 'google_drive';
  return 'other';
}

export function backingLabel(backing?: DataModelBacking): string {
  if (backing === 'postgres') return 'PostgreSQL';
  if (backing === 'mssql') return 'Microsoft SQL Server';
  if (backing === 'mysql') return 'MySQL';
  if (backing === 'google_drive') return 'Google Drive';
  return 'Connected store';
}

export function prettyJson(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return '';
  }
}

const JSON_SCHEMA_DRAFT = 'https://json-schema.org/draft/2020-12/schema';

function defKey(id: string): string {
  const cleaned = id.replace(/[^A-Za-z0-9_]/g, '_') || 'object';
  return /^[A-Za-z_]/.test(cleaned) ? cleaned : `obj_${cleaned}`;
}

function jsonFieldSchema(field: DataModelField): Record<string, unknown> {
  const raw = (field.dataType || 'string').toLowerCase();
  const schema: Record<string, unknown> = {
    type: 'string',
    description: field.displayName || field.name,
  };
  if (raw.includes('uuid')) {
    schema.type = 'string';
  } else if (raw.includes('bool')) {
    schema.type = ['boolean', 'string'];
  } else if ((raw.includes('int') || raw.includes('serial')) && !raw.includes('interval') && !raw.includes('point')) {
    schema.type = ['integer', 'string'];
  } else if (raw.includes('numeric') || raw.includes('decimal') || raw.includes('float') || raw.includes('double') || raw.includes('real') || raw.includes('money')) {
    schema.type = ['number', 'string'];
  } else if (raw.includes('timestamp') || raw.includes('datetime')) {
    schema.type = 'string';
    schema.format = 'date-time';
  } else if (raw.includes('date')) {
    schema.type = 'string';
    schema.format = 'date';
  }
  return schema;
}

function objectDef(table: DataModelTable): Record<string, unknown> {
  const fields = table.fields.length ? table.fields : table.kind === 'file' || table.kind === 'folder' ? STANDARD_FILE_FIELDS : [];
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  fields.forEach((field) => {
    if (!field.name) return;
    properties[field.name] = jsonFieldSchema(field);
    if (field.isPrimaryKey || field.nullable === false) required.push(field.name);
  });
  return {
    type: 'object',
    additionalProperties: true,
    description: table.name,
    properties: Object.keys(properties).length ? properties : { id: { type: 'string' } },
    required: required.length ? required : undefined,
  };
}

function stringifySchema(schema: Record<string, unknown>): string {
  return JSON.stringify(schema, null, 2);
}

export function composeClientGuidance(customContext?: string, useWhen?: string, doNotUseWhen?: string): string {
  const parts = [
    (customContext || '').trim(),
    (useWhen || '').trim() ? `Use when: ${(useWhen || '').trim()}` : '',
    (doNotUseWhen || '').trim() ? `Do not use when: ${(doNotUseWhen || '').trim()}` : '',
  ].filter(Boolean);
  return parts.join('\n\n');
}

export function withSchemaDescription(schemaJson: string, description: string): string {
  const desc = description.trim();
  if (!desc) return schemaJson;
  try {
    const parsed = JSON.parse(schemaJson || '{}') as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return schemaJson;
    return stringifySchema({ ...(parsed as Record<string, unknown>), description: desc });
  } catch {
    return schemaJson;
  }
}

export function parseClientGuidance(narrative: string): {
  labeled: boolean;
  customContext: string;
  useWhen: string;
  doNotUseWhen: string;
} {
  const text = (narrative || '').trim();
  if (!text) return { labeled: false, customContext: '', useWhen: '', doNotUseWhen: '' };

  const headingRe = /^(?:#{1,3}\s*)?(CONTEXT|USE WHEN|DO NOT USE WHEN)\s*:?[ \t]*/gim;
  const matches: { key: 'customContext' | 'useWhen' | 'doNotUseWhen'; start: number; body: number }[] = [];
  let match: RegExpExecArray | null;
  while ((match = headingRe.exec(text))) {
    const heading = match[1].toUpperCase();
    matches.push({
      key: heading === 'CONTEXT' ? 'customContext' : heading === 'USE WHEN' ? 'useWhen' : 'doNotUseWhen',
      start: match.index,
      body: match.index + match[0].length,
    });
  }
  if (!matches.length) {
    return { labeled: false, customContext: text, useWhen: '', doNotUseWhen: '' };
  }

  const next = { labeled: true, customContext: '', useWhen: '', doNotUseWhen: '' };
  matches.forEach((item, index) => {
    const end = index + 1 < matches.length ? matches[index + 1].start : text.length;
    next[item.key] = text.slice(item.body, end).trim();
  });
  return next;
}

export const CLIENT_GUIDANCE_FOLLOW_UP =
  'Write three labeled sections for an MCP client such as Claude. Use these exact headings on their own lines: CONTEXT, USE WHEN, DO NOT USE WHEN. CONTEXT explains what this capability is and how to use it. USE WHEN says when the client should call it. DO NOT USE WHEN says when it should not. Do not invent sources, tables, tools, or documents that are not listed.';

export function deriveCapabilityKind(input: {
  mcpCapability: McpCapabilityKind;
  sourceKind: PublishSourceKind;
  operations: DataModelOperation[];
  longRunning?: boolean;
}): SelficCapabilityKind {
  if (input.mcpCapability === 'resource') return 'information';
  if (input.sourceKind === 'external_mcp') return 'external_mcp';
  if (input.sourceKind === 'knowledge_base') return 'search';
  if (input.sourceKind === 'workflow') return input.longRunning ? 'process' : 'action';
  const writes = input.operations.some((op) => op === 'create' || op === 'update' || op === 'delete');
  if (writes) return 'action';
  if (input.operations.includes('query') && !input.operations.includes('read')) return 'search';
  return 'information';
}

function buildSqlSchemas(
  model: DataModelSource,
  tables: DataModelTable[],
  operations: DataModelOperation[]
): { inputSchema: string; outputSchema: string; resourceSchema: string } {
  const defs: Record<string, unknown> = {};
  const refs = tables.map((table) => {
    const key = defKey(table.id);
    defs[key] = objectDef(table);
    return { $ref: `#/$defs/${key}` };
  });
  const singleRef = refs[0] || { type: 'object' };
  const recordSchema = refs.length > 1 ? { oneOf: refs } : singleRef;
  const tableIds = tables.map((table) => table.id);
  const inputProps: Record<string, unknown> = {};
  const required: string[] = [];

  if (operations.length > 1) {
    inputProps.operation = { type: 'string', enum: operations, description: 'Data model operation to perform' };
    required.push('operation');
  }
  if (tableIds.length) {
    inputProps.table = {
      type: 'string',
      enum: tableIds,
      description: 'Selected table or entity',
    };
    if (tableIds.length > 1) required.push('table');
  }
  if (operations.includes('query')) {
    inputProps.query = {
      type: 'string',
      description: 'Optional search text matched against text columns. Do not send SQL.',
    };
    inputProps.limit = { type: 'number', description: 'Maximum rows to return' };
    inputProps.filters =
      tables.length === 1
        ? { ...objectDef(tables[0]), description: `Column filters for ${tables[0].name}` }
        : { type: 'object', additionalProperties: true, description: 'Column filters for the selected table' };
  }
  if (operations.includes('read') || operations.includes('update') || operations.includes('delete')) {
    inputProps.id = { type: 'string', description: 'Primary key of the row' };
  }
  if (operations.includes('create') || operations.includes('update')) {
    inputProps.record = recordSchema;
  }

  const inputSchema = stringifySchema({
    $schema: JSON_SCHEMA_DRAFT,
    title: `${model.name} input`,
    type: 'object',
    additionalProperties: false,
    properties: inputProps,
    required: required.length ? required : undefined,
    $defs: defs,
  });

  const outputSchema = stringifySchema({
    $schema: JSON_SCHEMA_DRAFT,
    title: `${model.name} output`,
    type: 'object',
    additionalProperties: false,
    properties: {
      status: { type: 'string' },
      count: { type: 'number' },
      rows: { type: 'array', items: recordSchema },
      record: recordSchema,
      deleted: { type: 'boolean' },
      message: { type: 'string' },
      detail: {},
    },
    $defs: defs,
  });

  const resourceSchema = stringifySchema({
    $schema: JSON_SCHEMA_DRAFT,
    title: `${model.name} resource`,
    type: 'object',
    additionalProperties: false,
    properties: {
      uri: { type: 'string' },
      model: { type: 'string', const: model.code },
      tables: { type: 'array', items: { type: 'string', enum: tableIds.length ? tableIds : undefined } },
      record: recordSchema,
    },
    $defs: defs,
  });

  return { inputSchema, outputSchema, resourceSchema };
}

function buildDriveSchemas(
  model: DataModelSource,
  objects: DataModelTable[],
  operations: DataModelOperation[]
): { inputSchema: string; outputSchema: string; resourceSchema: string } {
  const defs = { DriveFile: objectDef({ id: 'DriveFile', name: 'Drive file', kind: 'file', fields: STANDARD_FILE_FIELDS }) };
  const targets = objects.map((item) => item.id);
  const inputProps: Record<string, unknown> = {};
  const required: string[] = [];

  if (operations.length > 1) {
    inputProps.operation = { type: 'string', enum: operations, description: 'File operation to perform' };
    required.push('operation');
  }
  if (targets.length) {
    inputProps.target = {
      type: 'string',
      enum: targets,
      description: 'Selected Google Drive folder or file path',
    };
  }
  if (operations.includes('query')) {
    inputProps.query = { type: 'string', description: 'Search text within Drive' };
    inputProps.folder_id = { type: 'string', description: 'Folder to search' };
    inputProps.mime_type = { type: 'string', description: 'Optional MIME type filter' };
    inputProps.limit = { type: 'number', description: 'Maximum files to return' };
  }
  if (operations.includes('read') || operations.includes('update') || operations.includes('delete')) {
    inputProps.file_id = { type: 'string', description: 'Google Drive file id' };
    inputProps.path = { type: 'string', description: 'Full catalog path of the file' };
  }
  if (operations.includes('create') || operations.includes('update')) {
    inputProps.folder_id = { type: 'string', description: 'Destination folder' };
    inputProps.name = { type: 'string', description: 'File name' };
    inputProps.mime_type = { type: 'string' };
    inputProps.content = { type: 'string', description: 'File content or upload payload' };
  }

  const fileRef = { $ref: '#/$defs/DriveFile' };
  const inputSchema = stringifySchema({
    $schema: JSON_SCHEMA_DRAFT,
    title: `${model.name} input`,
    type: 'object',
    additionalProperties: false,
    properties: inputProps,
    required: required.length ? required : undefined,
    $defs: defs,
  });
  const outputSchema = stringifySchema({
    $schema: JSON_SCHEMA_DRAFT,
    title: `${model.name} output`,
    type: 'object',
    additionalProperties: false,
    properties: {
      status: { type: 'string' },
      count: { type: 'number' },
      files: { type: 'array', items: fileRef },
      file: fileRef,
      deleted: { type: 'boolean' },
      message: { type: 'string' },
      detail: {},
    },
    $defs: defs,
  });
  const resourceSchema = stringifySchema({
    $schema: JSON_SCHEMA_DRAFT,
    title: `${model.name} resource`,
    type: 'object',
    additionalProperties: false,
    properties: {
      uri: { type: 'string' },
      model: { type: 'string', const: model.code },
      targets: { type: 'array', items: { type: 'string', enum: targets.length ? targets : undefined } },
      file: fileRef,
    },
    $defs: defs,
  });
  return { inputSchema, outputSchema, resourceSchema };
}

function buildModelQuerySchemas(
  model: DataModelSource,
  tables: DataModelTable[],
  customContext?: string,
): { inputSchema: string; outputSchema: string; resourceSchema: string } {
  const tableNames = tables.map((table) => table.name).filter(Boolean);
  const columnHints = tables.flatMap((table) =>
    (table.fields || [])
      .map((field) => field.name)
      .filter(Boolean)
      .map((name) => `${table.name}.${name}`),
  );
  const allowed = [
    tableNames.length ? `Allowed tables: ${tableNames.join(', ')}.` : '',
    columnHints.length ? `Allowed columns: ${columnHints.join(', ')}.` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const queryHelp = [
    'Single read-only SELECT against the published tables. Joins, filters, and aggregations are allowed.',
    allowed,
  ]
    .filter(Boolean)
    .join(' ');
  const defs: Record<string, unknown> = {};
  const refs = tables.map((table) => {
    const key = defKey(table.id);
    defs[key] = objectDef(table);
    return { $ref: `#/$defs/${key}` };
  });
  const recordSchema = refs.length > 1 ? { oneOf: refs } : refs[0] || { type: 'object' };
  return {
    inputSchema: stringifySchema({
      $schema: JSON_SCHEMA_DRAFT,
      title: `${model.name} input`,
      description: (customContext || '').trim() || undefined,
      type: 'object',
      additionalProperties: false,
      properties: {
        query: {
          type: 'string',
          description: queryHelp,
        },
        limit: { type: 'number', description: 'Maximum rows to return' },
      },
      required: ['query'],
      $defs: defs,
    }),
    outputSchema: stringifySchema({
      $schema: JSON_SCHEMA_DRAFT,
      title: `${model.name} output`,
      type: 'object',
      additionalProperties: false,
      properties: {
        status: { type: 'string' },
        count: { type: 'number' },
        rows: { type: 'array', items: recordSchema },
        message: { type: 'string' },
        detail: {},
      },
      $defs: defs,
    }),
    resourceSchema: stringifySchema({
      $schema: JSON_SCHEMA_DRAFT,
      title: `${model.name} resource`,
      type: 'object',
      additionalProperties: false,
      properties: {
        uri: { type: 'string' },
        model: { type: 'string', const: model.code },
        tables: { type: 'array', items: { type: 'string', enum: tableNames.length ? tableNames : undefined } },
      },
      $defs: defs,
    }),
  };
}

export function buildDataModelSchemas(input: {
  model: DataModelSource;
  tables: DataModelTable[];
  operations: DataModelOperation[];
  capability: McpCapabilityKind;
  publishScope?: string;
  customContext?: string;
}): { inputSchema: string; outputSchema: string; resourceSchema: string } {
  const backing = input.model.backing || classifyBacking(input.model.connectorKey);
  const tables = input.tables.length ? input.tables : input.model.tables || [];
  const operations = input.operations.length ? input.operations : (['read'] as DataModelOperation[]);
  const schemas =
    input.publishScope === 'data_model' && input.capability === 'tool' && backing !== 'google_drive'
      ? buildModelQuerySchemas(input.model, tables, input.customContext)
      : backing === 'google_drive'
        ? buildDriveSchemas(input.model, tables, operations)
        : buildSqlSchemas(input.model, tables, operations);

  logGovernedMcp('app', 'publish_schema_built', {
    modelId: input.model.id,
    backing,
    objectCount: tables.length,
    fieldCount: tables.reduce((sum, item) => sum + item.fields.length, 0),
    kinds: tables.map((item) => item.kind || 'table'),
    operations,
    capability: input.capability,
  });

  return schemas;
}

export function buildKnowledgeBaseSchemas(name: string, description?: string): { inputSchema: string; outputSchema: string } {
  return {
    inputSchema: stringifySchema({
      type: 'object',
      description: (description || '').trim() || undefined,
      properties: {
        query: {
          type: 'string',
          description: 'Search text over the published documents. Returns matching passages, not live records.',
        },
        limit: { type: 'number', description: 'Maximum passages to return' },
      },
      required: ['query'],
    }),
    outputSchema: JSON.stringify(
      {
        type: 'object',
        properties: {
          knowledgeBase: { type: 'string', const: name },
          hits: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                title: { type: 'string' },
                snippet: { type: 'string' },
                score: { type: 'number' },
              },
            },
          },
        },
      },
      null,
      2
    ),
  };
}

export type WorkflowSchemaContract = {
  fields?: WorkflowInputField[];
  samplePayloadJson?: string | Record<string, unknown> | null;
  payloadKeys?: string[];
};

const ASK_IF_MISSING = 'Required. Ask the user if not explicitly provided. Never assume a value.';

export function parseWorkflowPayload(raw?: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw !== 'string' || !raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function stringifyWorkflowPayload(raw?: unknown): string | null {
  const parsed = parseWorkflowPayload(raw);
  if (Object.keys(parsed).length) return JSON.stringify(parsed);
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return '{}';
  if (typeof raw === 'string' && raw.trim() === '{}') return '{}';
  return null;
}

export function isBlankWorkflowTemplate(sample: Record<string, unknown>): boolean {
  const values = Object.values(sample);
  if (!values.length) return false;
  return values.every((value) => value === '' || value == null || (Array.isArray(value) && value.length === 0));
}

export function isLeftoverDemoSample(sample: Record<string, unknown>): boolean {
  const keys = Object.keys(sample);
  if (keys.length !== 1 || keys[0] !== 'query') return false;
  return /refund policy/i.test(String(sample.query ?? ''));
}

function blankValue(value: unknown): unknown {
  if (Array.isArray(value)) return [];
  if (value && typeof value === 'object') {
    const nested: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
      nested[key] = blankValue(item);
    });
    return nested;
  }
  return '';
}

export function workflowContractKeys(
  fields?: WorkflowInputField[],
  samplePayloadJson?: string | Record<string, unknown> | null,
  payloadKeys?: string[],
): string[] {
  const keys: string[] = [];
  const add = (key?: string) => {
    const name = (key || '').trim();
    if (!name || keys.includes(name)) return;
    keys.push(name);
  };
  (payloadKeys || []).forEach(add);
  (fields || []).forEach((field) => add(field.key));
  const sample = parseWorkflowPayload(samplePayloadJson);
  if ((!keys.length || isBlankWorkflowTemplate(sample)) && !isLeftoverDemoSample(sample)) {
    Object.keys(sample).forEach(add);
  }
  return keys;
}

export function workflowTriggerPayload(
  fields?: WorkflowInputField[],
  samplePayloadJson?: string | Record<string, unknown> | null,
  payloadKeys?: string[],
): Record<string, unknown> {
  const sample = parseWorkflowPayload(samplePayloadJson);
  const payload: Record<string, unknown> = {};
  workflowContractKeys(fields, samplePayloadJson, payloadKeys).forEach((key) => {
    payload[key] = key in sample ? blankValue(sample[key]) : '';
  });
  return payload;
}

function jsonSchemaType(dataType?: string): string {
  const value = String(dataType || 'string').toLowerCase();
  if (value.includes('int') || value.includes('number') || value.includes('float')) return 'number';
  if (value.includes('bool')) return 'boolean';
  if (value.includes('array')) return 'array';
  if (value.includes('object')) return 'object';
  return 'string';
}

function askDescription(help?: string): string {
  const label = (help || '').trim();
  return label ? `${label}. ${ASK_IF_MISSING}` : ASK_IF_MISSING;
}

function sampleSchema(value: unknown): Record<string, unknown> {
  if (Array.isArray(value)) {
    return { type: 'array', items: value.length ? sampleSchema(value[0]) : { type: 'string' } };
  }
  if (value && typeof value === 'object') return objectFromSample(value as Record<string, unknown>);
  if (typeof value === 'number') return { type: 'number' };
  if (typeof value === 'boolean') return { type: 'boolean' };
  return { type: 'string' };
}

function objectFromSample(value: Record<string, unknown>): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  Object.entries(value).forEach(([key, item]) => {
    properties[key] = sampleSchema(item);
    required.push(key);
  });
  return {
    type: 'object',
    additionalProperties: false,
    properties,
    required: required.length ? required : undefined,
  };
}

function fieldProperty(field: WorkflowInputField | undefined, value: unknown): Record<string, unknown> {
  const schema = value !== undefined ? sampleSchema(value) : { type: jsonSchemaType(field?.dataType) };
  if (field?.options?.length) schema.enum = field.options;
  schema.description = askDescription(field?.helpText || field?.label);
  return schema;
}

export function buildWorkflowSchemas(
  process: boolean,
  description?: string,
  contract?: WorkflowSchemaContract | null,
): { inputSchema: string; outputSchema: string } {
  const outputSchema = JSON.stringify(
    {
      type: 'object',
      properties: {
        runId: { type: 'string' },
        status: { type: 'string' },
        ...(process ? { taskId: { type: 'string' } } : {}),
      },
      required: process ? ['runId', 'taskId', 'status'] : ['runId', 'status'],
    },
    null,
    2
  );
  if (!contract) {
    return {
      inputSchema: stringifySchema({
        type: 'object',
        description: (description || '').trim() || undefined,
        properties: {
          workflowId: { type: 'string', description: 'Process to start' },
          input: { type: 'object', description: 'Input for this action' },
        },
        required: ['workflowId'],
      }),
      outputSchema,
    };
  }
  const sample = parseWorkflowPayload(contract.samplePayloadJson);
  const fields = (contract.fields || []).filter((field) => field.key);
  const fieldByKey = new Map(fields.map((field) => [field.key, field]));
  const keys = workflowContractKeys(fields, contract.samplePayloadJson, contract.payloadKeys);
  const properties: Record<string, Record<string, unknown>> = {};
  keys.forEach((key) => {
    properties[key] = fieldProperty(fieldByKey.get(key), sample[key]);
  });
  const required = keys;
  logGovernedMcp('app', 'publish_workflow_schema_built', {
    sampleKeys: Object.keys(sample).length,
    required: required.length,
    empty: required.length === 0,
  });
  if (!required.length) {
    return {
      inputSchema: stringifySchema({
        type: 'object',
        additionalProperties: false,
        description: (description || '').trim() || undefined,
        properties: {},
      }),
      outputSchema,
    };
  }
  return {
    inputSchema: stringifySchema({
      type: 'object',
      additionalProperties: false,
      description: (description || '').trim() || undefined,
      properties,
      required,
    }),
    outputSchema,
  };
}
